import { useEffect, useState } from 'react';
import {
  Button, Card, Dialog, DialogPanel,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import type { CashTicket } from '../api/types';
import { fetchCashTickets } from '../api/cashReport';
import { t } from '../lib/i18n';

const fmtMxn = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(n);

interface Props {
  open: boolean;
  onClose: () => void;
  from: string;
  to: string;
  sid?: number;
  date?: string;
  tender?: string;
  cashier?: string;
  title: string;
}

export default function CashTicketsModal({ open, onClose, from, to, sid, date, tender, cashier, title }: Props) {
  const [tickets, setTickets] = useState<CashTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    setTickets([]);
    fetchCashTickets({ from, to, sid, date, tender, cashier })
      .then((rows) => { if (alive) setTickets(rows); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, from, to, sid, date, tender, cashier]);

  return (
    <Dialog open={open} onClose={onClose} static={true}>
      <DialogPanel>
        <Title>{title}</Title>
        {loading && <Text className="mt-4">…</Text>}
        {err && <Text className="mt-4 text-red-600 dark:text-red-400">{err}</Text>}
        {!loading && !err && tickets.length === 0 && (
          <Text className="mt-4">{t.caja.empty}</Text>
        )}
        {!loading && tickets.length > 0 && (
          <Card className="mt-4 p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.caja.cols.folio}</TableHeaderCell>
                  <TableHeaderCell>{t.caja.cols.date}</TableHeaderCell>
                  <TableHeaderCell>{t.caja.cols.client}</TableHeaderCell>
                  <TableHeaderCell>{t.caja.cols.method}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.caja.cols.amount}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((tk, i) => (
                  <TableRow key={`${tk.tnum}-${tk.sid}-${i}`}>
                    <TableCell className="font-mono text-xs">{tk.tnum}</TableCell>
                    <TableCell>{tk.date}</TableCell>
                    <TableCell>{tk.name || String(tk.anum)}</TableCell>
                    <TableCell>{tk.code}</TableCell>
                    <TableCell className="text-right">{fmtMxn(tk.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
        <div className="mt-6 flex justify-end">
          <Button onClick={onClose}>{t.common.close}</Button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
